import { AssetRecordType } from "@tldraw/tldraw";
import React, { useState } from "react";
import { useIsBuilderEmptyState } from "pages/flyer/builder/hooks/useIsBuilderEmptyState";

interface ItemPhotoProps {
  photo: {
    id: string;
    width: number;
    height: number;
    urls: {
      regular: string;
    };
    alt_description: string;
  };
  editor: any;
  format: any;
  onImageSelected?: () => void;
  saveToBrandKit?: (photoUrl: string, photoName: string) => void;
}

const Item = ({ photo, editor, format, onImageSelected, saveToBrandKit }: ItemPhotoProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const isEmptyState = useIsBuilderEmptyState();
  const orientation = photo.width > photo.height ? "landscape" : "portrait";

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    if (isEmptyState) return;
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling that might interfere with selection

    const initialX = e.clientX;
    const initialY = e.clientY;
    let hasMoved = false;

    const canvas = document.querySelector(".tl-canvas") as HTMLElement;
    if (canvas) {
      canvas.style.setProperty("cursor", "grabbing");
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - initialX);
      const deltaY = Math.abs(moveEvent.clientY - initialY);

      if (deltaX > 5 || deltaY > 5) {
        hasMoved = true;
        setIsDragging(true);
        editor.setCursor({ type: "move" });
      }
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      if (hasMoved) {
        // Drag-and-drop action - place image at drop location
        handleClickedImage(id, upEvent);
      } else {
        // Click action - place image at center
        handleClickedImage(id);
      }

      setIsDragging(false);
      editor.setCursor({ type: "default" });

      if (canvas) {
        canvas.style.removeProperty("cursor");
      }

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleClickedImage = (id: string, upEvent?: MouseEvent) => {
    // When triggered from Brand Kit, only save — don't place on canvas
    if (saveToBrandKit) {
      const name = photo.alt_description || `unsplash-${photo.id}`;
      saveToBrandKit(photo.urls.regular, name);
      if (onImageSelected) {
        onImageSelected();
      }
      return;
    }

    const assetId = AssetRecordType.createId();
    const aspectRatio = photo.width / photo.height;
    // get center of the screen and convert it to page coordinates
    const { width, height } = editor.getViewportScreenBounds();
    const center = editor.screenToPage({ x: width / 2, y: height / 2 });

    let imageWidth, imageHeight;

    if (orientation === "landscape") {
      imageWidth = format.displayWidth;
      imageHeight = imageWidth / aspectRatio;
    } else {
      imageHeight = format.displayHeight;
      imageWidth = imageHeight * aspectRatio;
    }

    // Create asset
    editor.createAssets([
      {
        id: assetId,
        type: "image",
        typeName: "asset",
        props: {
          name: photo.alt_description,
          src: photo.urls.regular,
          w: imageWidth,
          h: imageHeight,
          mimeType: "image/png",
          isAnimated: false,
        },
        meta: {
          name: photo.alt_description,
        },
      },
    ]);

    // createShapes
    const shapeId = `shape:${photo.id + Date.now()}`;
    editor.createShapes([
      {
        id: shapeId,
        type: "customImage",
        props: {
          assetId,
          w: imageWidth,
          h: imageHeight,
        },
        x: upEvent
          ? editor.screenToPage({ x: upEvent.clientX, y: upEvent.clientY }).x - imageWidth / 2
          : center.x - imageWidth / 2,
        y: upEvent
          ? editor.screenToPage({ x: upEvent.clientX, y: upEvent.clientY }).y - imageHeight / 2
          : center.y - imageHeight / 2,
      },
    ]);

    // Switch to select tool and select the newly created image
    editor.setCurrentTool("select");
    editor.select(shapeId);

    // Call callback to reset search if provided
    if (onImageSelected) {
      onImageSelected();
    }
  };

  return (
    <>
      {isDragging && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white text-lg z-50 pointer-events-none cursor-moved">
          Drag and Drop image
        </div>
      )}
      <img
        className="object-cover cursor-pointer transition-all w-full h-full min-h-[87px] min-w-[87px] hover:scale-[1.2]"
        src={photo.urls.regular}
        alt={photo.alt_description}
        onMouseDown={(e) => handleMouseDown(e, photo.id)}
      />
    </>
  );
};

export default Item;
